/**
 * CenterFittingsUI.js
 * 
 * React компонент для UI управления центральными фитингами
 * (термометры и лицевые подключения)
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Grid,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * Компонент UI для центральных фитингов
 * 
 * @param {Object} props - Свойства компонента
 * @param {Array} props.centerFittings - Массив центральных фитингов
 * @param {number} props.centerFittingCount - Количество центральных фитингов
 * @param {Function} props.onUpdateCount - Обработчик изменения количества фитингов
 * @param {Function} props.onUpdateFitting - Обработчик обновления параметров фитинга
 * @param {Function} props.onDeleteFitting - Обработчик удаления фитинга
 * @param {Array} props.centerFittingCategories - Массив категорий фитингов
 * @param {Function} props.getFittingsByCategory - Функция получения фитингов по категории
 * @param {Function} props.isFittingCompatibleWithMainPipe - Функция проверки совместимости для круглых труб
 * @param {Function} props.isFittingCompatibleWithSquareProfile - Функция проверки совместимости для квадратных профилей
 * @param {number} props.mainPipeDiameter - Диаметр основной трубы
 * @param {string} props.shape - Форма трубы: 'round' или 'square'
 * @param {string} props.mainPipeProfile - Профиль квадратной трубы (например, '60x60')
 * @param {number} props.circuitCount - Количество контуров (для "между контурами")
 * @param {number} props.bottomCircuitCount - Количество нижних контуров (для этажного коллектора)
 * @param {Function} props.onIncompatibilityWarning - Обработчик предупреждения о несовместимости
 * @param {string} props.mode - Режим работы: 'floor' (для этажного) или 'distribution' (для распределительного)
 * @param {string} props.currentRow - Текущая линия: 'supply' (подача), 'return' (обратка), 'top' (верх), 'bottom' (низ)
 */
function CenterFittingsUI({
  centerFittings = [],
  centerFittingCount = 0,
  onUpdateCount,
  onUpdateFitting,
  onDeleteFitting,
  centerFittingCategories = [],
  getFittingsByCategory,
  isFittingCompatibleWithMainPipe,
  isFittingCompatibleWithSquareProfile,
  mainPipeDiameter,
  shape = 'round',
  mainPipeProfile = '60x60',
  circuitCount = 0,
  bottomCircuitCount = 0,
  onIncompatibilityWarning,
  mode = 'floor', // По умолчанию режим этажного коллектора
  currentRow = 'supply', // Текущая линия (supply/return для distribution, top/bottom для floor)
}) {
  // Определяем варианты размещения в зависимости от режима
  const placementOptions = mode === 'distribution' 
    ? [
        { value: 'center', label: 'По середине' },
        { value: 'betweenCircuits', label: 'Между контурами' },
        { value: 'betweenFittings', label: 'Между патрубками' },
        { value: 'distance', label: 'На расстоянии' },
      ]
    : [
        { value: 'center', label: 'По середине' },
        { value: 'between', label: 'Между контурами' },
        { value: 'distance', label: 'На расстоянии' },
      ];
  
  // Определяем количество вариантов для выбора
  const getOptionsCount = (positionType, referenceRow = 'supply') => {
    if (mode === 'distribution') {
      // Для распределительного коллектора учитываем выбранную линию (подача/обратка)
      // Если текущая линия совпадает с referenceRow, используем circuitCount, иначе bottomCircuitCount
      const count = referenceRow === currentRow ? circuitCount : bottomCircuitCount;
      
      if (positionType === 'betweenCircuits') {
        return Math.max(0, count - 1); // Между контурами (минимум 0)
      } else if (positionType === 'betweenFittings') {
        return Math.max(0, count * 2 - 1); // Между патрубками (минимум 0)
      }
    } else {
      if (positionType === 'between') {
        // Для этажного коллектора учитываем выбранную трубу (top/bottom)
        const count = referenceRow === 'bottom' ? bottomCircuitCount : circuitCount;
        return Math.max(0, count - 1); // Между контурами (этажный) (минимум 0)
      }
    }
    return 0;
  };
  return (
    <>
      <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
        Фитинги термометров и лицевых подключений
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Количество:</Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateCount(-1)}
              disabled={centerFittingCount <= 0}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
              {centerFittingCount}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateCount(1)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Таблица центральных фитингов */}
      {centerFittingCount > 0 && (
        <TableContainer sx={{ maxHeight: '13.75rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Размещение</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Сторона</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Значение</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 60 }}>
                  {mode === 'distribution' ? 'Линия' : 'Контуры'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {centerFittings.map((fitting, index) => {
                const fittingsInCategory = getFittingsByCategory(
                  fitting.fittingCategory || 'Муфты',
                  mainPipeDiameter,
                  shape,
                  mainPipeProfile
                );
                
                return (
                  <TableRow key={fitting.id}>
                    <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                    
                    {/* Категория */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={fitting.fittingCategory || 'Муфты'}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          const fittingsInNewCategory = getFittingsByCategory(newCategory, mainPipeDiameter, shape, mainPipeProfile);
                          const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                          
                          if (!firstCompatibleFitting) {
                            onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                            return;
                          }
                          
                          onUpdateFitting(index, {
                            fittingCategory: newCategory,
                            fittingId: firstCompatibleFitting.id,
                            fittingLength: null,
                            flangePipeLength: null,
                          });
                        }}
                        sx={{ minWidth: 120, fontSize: '0.8rem' }}
                      >
                        {centerFittingCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    
                    {/* Фитинг */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={fitting.fittingId || ''}
                        onChange={(e) => {
                          const newFittingId = e.target.value;
                          // Всегда используем isFittingCompatibleWithMainPipe — она учитывает компактный режим
                          const isCompatible = isFittingCompatibleWithMainPipe(newFittingId, mainPipeDiameter);
                          
                          if (!isCompatible) {
                            onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                            return;
                          }
                          onUpdateFitting(index, {
                            fittingId: e.target.value,
                            fittingLength: null,
                            flangePipeLength: null,
                          });
                        }}
                        sx={{ minWidth: 120, fontSize: '0.8rem' }}
                      >
                        {fittingsInCategory.map((f) => (
                          <MenuItem 
                            key={f.id} 
                            value={f.id}
                            onClick={(e) => {
                              if (!f.isCompatible) {
                                e.preventDefault();
                                e.stopPropagation();
                                onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                              }
                            }}
                            sx={{
                              fontSize: '0.8rem',
                              ...(!f.isCompatible && {
                                color: 'text.disabled',
                                textDecoration: 'line-through',
                                backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                              }),
                            }}
                          >
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    <TableCell sx={{ p: 0.5 }}>
                      {/^(Flanec_|Nak_Gaika_|Shaib_Nar_Rezba_)/.test(fitting.fittingId || '') ? (
                        <TextField
                          type="number"
                          size="small"
                          value={fitting.flangePipeLength === 0 ? '' : (fitting.flangePipeLength ?? '')}
                          onChange={(e) => onUpdateFitting(index, {
                            flangePipeLength: e.target.value === '' ? null : Number(e.target.value),
                          })}
                          placeholder="Авто"
                          sx={{ width: 85 }}
                          InputProps={{
                            endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                            sx: { fontSize: '0.8rem' },
                          }}
                        />
                      ) : (
                        <TextField
                          type="number"
                          size="small"
                          value={fitting.fittingLength === 0 ? '' : (fitting.fittingLength ?? '')}
                          onChange={(e) => onUpdateFitting(index, {
                            fittingLength: e.target.value === '' ? null : Number(e.target.value),
                          })}
                          placeholder="Авто"
                          sx={{ width: 85 }}
                          InputProps={{
                            endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                            sx: { fontSize: '0.8rem' },
                          }}
                        />
                      )}
                    </TableCell>
                    
                    {/* Размещение */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={fitting.positionType || 'center'}
                        onChange={(e) => {
                          const newPositionType = e.target.value;
                          // При смене типа размещения сбрасываем betweenIndex на 0
                          if (newPositionType === 'betweenCircuits' || newPositionType === 'betweenFittings' || newPositionType === 'between') {
                            onUpdateFitting(index, { positionType: newPositionType, betweenIndex: 0 });
                          } else {
                            onUpdateFitting(index, 'positionType', newPositionType);
                          }
                        }}
                        sx={{ minWidth: 120, fontSize: '0.8rem' }}
                      >
                        {placementOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    
                    {/* Сторона */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={fitting.side || 'both'}
                        onChange={(e) => onUpdateFitting(index, 'side', e.target.value)}
                        sx={{ minWidth: 110, fontSize: '0.8rem' }}
                      >
                        <MenuItem value="both">С 2-х сторон</MenuItem>
                        <MenuItem value="front">Спереди</MenuItem>
                        <MenuItem value="back">Сзади</MenuItem>
                      </Select>
                    </TableCell>
                    
                    {/* Значение (динамическое поле) */}
                    <TableCell sx={{ p: 0.5 }}>
                      {(fitting.positionType === 'between' || 
                        fitting.positionType === 'betweenCircuits' || 
                        fitting.positionType === 'betweenFittings') ? (
                        (() => {
                          const optionsCount = getOptionsCount(fitting.positionType, fitting.referenceRow);
                          if (optionsCount === 0) {
                            return (
                              <Tooltip title="Недостаточно контуров для размещения между ними">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.8rem', 
                                    color: 'error.main',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  Нет вариантов
                                </Typography>
                              </Tooltip>
                            );
                          }
                          return (
                            <Select
                              size="small"
                              value={fitting.betweenIndex ?? 0}
                              onChange={(e) => onUpdateFitting(index, 'betweenIndex', e.target.value)}
                              sx={{ minWidth: 80, fontSize: '0.8rem' }}
                            >
                              {Array.from({ length: optionsCount }, (_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i + 1} и {i + 2}
                                </MenuItem>
                              ))}
                            </Select>
                          );
                        })()
                      ) : fitting.positionType === 'center' ? (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          —
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={fitting.distanceFromStart === 0 ? '' : (fitting.distanceFromStart ?? 50)}
                            onChange={(e) => onUpdateFitting(index, 'distanceFromStart', parseFloat(e.target.value) || 0)}
                            sx={{ width: 85 }}
                            InputProps={{ 
                              endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                              sx: { fontSize: '0.8rem' }
                            }}
                            title="Расстояние от начала гребенки"
                          />
                          <Tooltip title={fitting.distanceDirection === 'right' ? 'Отсчёт справа' : 'Отсчёт слева'}>
                            <IconButton
                              size="small"
                              onClick={() => onUpdateFitting(index, 'distanceDirection', fitting.distanceDirection === 'right' ? 'left' : 'right')}
                              sx={{ 
                                p: 0.3,
                                color: fitting.distanceDirection === 'right' ? 'primary.main' : 'text.secondary',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                    
                    {/* Контуры/Линия (для этажного и распределительного коллектора) */}
                    <TableCell sx={{ p: 0.5, textAlign: 'center' }}>
                      {mode === 'floor' ? (
                        // Этажный коллектор: верхние/нижние контуры
                        fitting.positionType === 'between' ? (
                          <Tooltip title={fitting.referenceRow === 'bottom' ? 'Нижние контуры' : 'Верхние контуры'}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newValue = fitting.referenceRow === 'bottom' ? 'top' : 'bottom';
                                onUpdateFitting(index, { referenceRow: newValue, betweenIndex: 0 });
                              }}
                              sx={{ 
                                p: 0.5,
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              {fitting.referenceRow === 'bottom' ? (
                                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            —
                          </Typography>
                        )
                      ) : (
                        // Распределительный коллектор: подача/обратка
                        (fitting.positionType === 'betweenCircuits' || fitting.positionType === 'betweenFittings') ? (
                          <Tooltip title={fitting.referenceRow === 'return' ? 'Обратная линия' : 'Подающая линия'}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newValue = fitting.referenceRow === 'return' ? 'supply' : 'return';
                                onUpdateFitting(index, { referenceRow: newValue, betweenIndex: 0 });
                              }}
                              sx={{ 
                                p: 0.5,
                                color: fitting.referenceRow === 'return' ? '#2196f3' : '#f44336',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              {fitting.referenceRow === 'return' ? (
                                <ArrowBackIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            —
                          </Typography>
                        )
                      )}
                    </TableCell>
                    
                    {/* Удалить */}
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteFitting(index)}
                        sx={{ color: 'error.main' }}
                        title="Удалить фитинг"
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

export default CenterFittingsUI;

